import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DollarSign, TrendingUp, TrendingDown, Users, Search, 
  Play, Clock, CheckCircle, Calendar, Zap, Eye
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { 
  getAllUsers, addProfit, addLoss, 
  distributeDailyProfits, getProfitDistributions, simulateProfits 
} from '../services/api';
import { toast } from 'sonner';

const AdminProfitsPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [profitData, setProfitData] = useState({
    amount: '',
    isPercentage: false,
    description: ''
  });
  const [isLoss, setIsLoss] = useState(false);
  
  // Automated profit states
  const [distributions, setDistributions] = useState([]);
  const [distributing, setDistributing] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [activeTab, setActiveTab] = useState('manual');

  useEffect(() => {
    fetchUsers();
    fetchDistributions();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await getAllUsers({ status: 'active' });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchDistributions = async () => {
    try {
      const response = await getProfitDistributions(30);
      setDistributions(response.data);
    } catch (error) {
      console.error('Failed to load distributions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        user_id: selectedUser.id,
        amount: parseFloat(profitData.amount),
        is_percentage: profitData.isPercentage,
        description: profitData.description || (isLoss ? 'Trading loss' : 'Trading profit')
      };

      if (isLoss) {
        await addLoss(data);
        toast.success('Loss recorded successfully');
      } else {
        await addProfit(data);
        toast.success('Profit added successfully');
      }

      setSelectedUser(null);
      setProfitData({ amount: '', isPercentage: false, description: '' });
      fetchUsers();
    } catch (error) {
      toast.error('Failed to process');
    }
  };

  const handleDistributeProfits = async () => {
    if (!window.confirm('Are you sure you want to distribute daily profits to all active investments?')) {
      return;
    }
    
    setDistributing(true);
    try {
      const response = await distributeDailyProfits();
      toast.success(`${response.data.message} - $${response.data.total_distributed?.toFixed(2)} distributed to ${response.data.investments_processed} investments`);
      fetchDistributions();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to distribute profits';
      toast.error(message);
    } finally {
      setDistributing(false);
    }
  };

  const handleSimulate = async () => {
    setSimulating(true);
    try {
      const response = await simulateProfits();
      setSimulation(response.data);
      toast.success('Simulation complete');
    } catch (error) {
      toast.error('Failed to simulate');
    } finally {
      setSimulating(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <div data-testid="admin-profits-page">
        {/* Header */}
        <div className="admin-page-header mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] text-white">
            Profit Management
          </h1>
          <p className="text-slate-400">Manage manual and automated profit distribution</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 sm:gap-4 mb-8">
          <button
            onClick={() => setActiveTab('manual')}
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all text-sm sm:text-base ${
              activeTab === 'manual' 
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' 
                : 'bg-white/5 text-slate-400 hover:bg-white/10'
            }`}
          >
            Manual Profits
          </button>
          <button
            onClick={() => setActiveTab('automated')}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'automated' 
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' 
                : 'bg-white/5 text-slate-400 hover:bg-white/10'
            }`}
            data-testid="automated-tab"
          >
            Automated Daily Profits
          </button>
        </div>

        {/* Automated Tab Content */}
        {activeTab === 'automated' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Action Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Distribute Profits Card */}
              <div className="glass-card p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center">
                    <Zap className="w-7 h-7 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Distribute Daily Profits</h3>
                    <p className="text-slate-400 text-sm">Apply profits to all active investments</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-6">
                  This will calculate and add daily profits to all active investments based on each trader's 
                  configured profit percentage. Each user will receive a notification.
                </p>
                <button
                  onClick={handleDistributeProfits}
                  disabled={distributing}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="distribute-profits-btn"
                >
                  {distributing ? (
                    <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Distribute Profits Now
                    </>
                  )}
                </button>
              </div>

              {/* Simulate Card */}
              <div className="glass-card p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                    <Eye className="w-7 h-7 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Simulate Distribution</h3>
                    <p className="text-slate-400 text-sm">Preview before applying</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-6">
                  Preview what profits would be distributed without actually applying them. 
                  Useful for verification before running the actual distribution.
                </p>
                <button
                  onClick={handleSimulate}
                  disabled={simulating}
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                  data-testid="simulate-profits-btn"
                >
                  {simulating ? (
                    <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      Run Simulation
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Simulation Results */}
            {simulation && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6 mb-8"
              >
                <h3 className="text-xl font-bold text-white mb-4">Simulation Results</h3>
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                    <p className="text-slate-400 text-sm">Total to Distribute</p>
                    <p className="text-2xl font-bold text-emerald-400">{formatCurrency(simulation.simulated_total)}</p>
                  </div>
                  <div className="p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/20">
                    <p className="text-slate-400 text-sm">Investments Affected</p>
                    <p className="text-2xl font-bold text-cyan-400">{simulation.investment_count}</p>
                  </div>
                </div>
                
                {simulation.investments?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Trader</th>
                          <th>Current Value</th>
                          <th>Profit %</th>
                          <th>Projected Profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {simulation.investments.map((inv, index) => (
                          <tr key={index}>
                            <td>
                              <p className="text-white">{inv.user_name}</p>
                              <p className="text-slate-400 text-xs">{inv.user_email}</p>
                            </td>
                            <td className="text-white">{inv.trader_name}</td>
                            <td className="text-white">{formatCurrency(inv.current_value)}</td>
                            <td className="text-cyan-400">{inv.profit_percentage}%</td>
                            <td className="text-emerald-400 font-semibold">+{formatCurrency(inv.projected_profit)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            )}

            {/* Distribution History */}
            <div className="glass-card p-6">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-cyan-400" />
                Distribution History
              </h3>
              
              {distributions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Total Distributed</th>
                        <th>Investments</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {distributions.map((dist) => (
                        <tr key={dist.id}>
                          <td className="text-white font-medium">{formatDate(dist.date)}</td>
                          <td className="text-emerald-400 font-semibold">{formatCurrency(dist.total_distributed)}</td>
                          <td className="text-white">{dist.investments_processed}</td>
                          <td>
                            <span className="badge badge-success">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Completed
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No distributions yet</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Manual Tab Content */}
        {activeTab === 'manual' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-12 w-full"
                  placeholder="Search users..."
                  data-testid="search-users"
                />
              </div>
            </div>

            {/* Users Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <div key={i} className="glass-card p-6">
                    <div className="skeleton h-12 w-12 rounded-xl mb-4" />
                    <div className="skeleton h-6 w-3/4 mb-2" />
                    <div className="skeleton h-4 w-1/2" />
                  </div>
                ))
              ) : (
                filteredUsers.map((user) => (
                  <motion.div
                    key={user.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="glass-card p-6"
                    data-testid={`user-profit-card-${user.id}`}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
                        <span className="text-gray-900 font-bold text-lg">
                          {user.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{user.name}</h3>
                        <p className="text-slate-400 text-sm">{user.email}</p>
                      </div>
                    </div>

                    <div className="bg-white/5 rounded-xl p-4 mb-4">
                      <p className="text-slate-400 text-sm mb-1">Current Balance</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(user.balance)}</p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => { setSelectedUser(user); setIsLoss(false); }}
                        className="btn-primary flex-1 flex items-center justify-center gap-2 py-3"
                        data-testid={`add-profit-${user.id}`}
                      >
                        <TrendingUp className="w-4 h-4" />
                        Add Profit
                      </button>
                      <button
                        onClick={() => { setSelectedUser(user); setIsLoss(true); }}
                        className="btn-secondary flex-1 flex items-center justify-center gap-2 py-3 text-red-400 border-red-500/20"
                        data-testid={`add-loss-${user.id}`}
                      >
                        <TrendingDown className="w-4 h-4" />
                        Add Loss
                      </button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {filteredUsers.length === 0 && !loading && (
              <div className="text-center py-20">
                <Users className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                <h3 className="text-xl font-semibold text-white mb-2">No users found</h3>
                <p className="text-slate-400">Try adjusting your search</p>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Profit/Loss Modal */}
      <AnimatePresence>
        {selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-overlay"
            onClick={() => setSelectedUser(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="profit-modal"
            >
              <h2 className="text-2xl font-bold text-white mb-2">
                {isLoss ? 'Add Loss' : 'Add Profit'}
              </h2>
              <p className="text-slate-400 mb-6">
                For: <strong className="text-white">{selectedUser.name}</strong>
              </p>

              <div className="p-4 bg-white/5 rounded-xl mb-6">
                <p className="text-slate-400 text-sm">Current Balance</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(selectedUser.balance)}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Amount</label>
                  <input
                    type="number"
                    value={profitData.amount}
                    onChange={(e) => setProfitData({ ...profitData, amount: e.target.value })}
                    className="input-field"
                    placeholder={profitData.isPercentage ? 'Enter percentage' : 'Enter amount'}
                    min="0.01"
                    step="0.01"
                    required
                    data-testid="profit-amount"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={profitData.isPercentage}
                      onChange={(e) => setProfitData({ ...profitData, isPercentage: e.target.checked })}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-slate-300">As Percentage of Balance</span>
                  </label>
                </div>

                {profitData.isPercentage && profitData.amount && (
                  <div className="p-3 bg-cyan-500/10 rounded-xl border border-cyan-500/20">
                    <p className="text-cyan-400 text-sm">
                      Calculated amount: {formatCurrency(selectedUser.balance * (parseFloat(profitData.amount) / 100))}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Description (Optional)</label>
                  <input
                    type="text"
                    value={profitData.description}
                    onChange={(e) => setProfitData({ ...profitData, description: e.target.value })}
                    className="input-field"
                    placeholder={isLoss ? 'Reason for loss' : 'Reason for profit'}
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button type="button" onClick={() => setSelectedUser(null)} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`flex-1 flex items-center justify-center gap-2 ${
                      isLoss ? 'bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors' : 'btn-primary'
                    }`}
                    data-testid="submit-profit"
                  >
                    {isLoss ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                    {isLoss ? 'Add Loss' : 'Add Profit'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

export default AdminProfitsPage;
